import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * meetingIntelligence module ServiceStores.
 * Declares the services its ViewStores consume (migrated off the removed
 * createRestApi). Each entry: { name: <feathers service path>, options }.
 *
 * NOTE: the `user` service is intentionally NOT declared here — RootStore mounts
 * a builtin `userStore` against the singular `user` path (see builtInStores.ts),
 * and a duplicate config would point at the same path. The ReviewQueue store
 * reads `rootStore.userStore` for its reassign picker.
 */
export const meetingIntelligenceStores: ServiceStoreConfig[] = [
  // Ingested Read.ai meeting sessions. get(id) embeds participants[]/actionItems[].
  { name: "read-ai-meetings", options: { idField: "id", enableStorage: false } },
  // The review of one processed meeting (plan/2026-10-21_FROM-GIRDER_meeting-review-and-cards.md
  // §2), keyed by the MEETING's id: get only. Responses go through the RAW Feathers client
  // (the API answers them with an envelope, `{ review, settled?, detail? }`, and the raw client
  // rejects every refusal), and the view re-reads the review with `get` afterwards.
  //   - no initial find (there is no find), no storage, no offline copy;
  //   - `throwOnError`: "could not load" must never read as "nothing to review".
  {
    name: "meeting-reviews",
    options: {
      idField: "id",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["get"],
      throwOnError: true,
    },
  },
  // A meeting's recap email (#3182), keyed by the MEETING's id: get only, with
  // `?audience=internal|client`. The draft is composed by the server; the send
  // goes through the RAW Feathers client (MeetingRecapStore), whose refusals reject.
  //   - no initial find (there is no find), no storage, no offline copy: a draft
  //     names recipients and must not outlive the session on the device;
  //   - `throwOnError`: "could not draft" must never read as an empty recap.
  {
    name: "meeting-recaps",
    options: {
      idField: "id",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["get"],
      throwOnError: true,
    },
  },
  // A meeting's open questions, for Meeting Detail → Client questions (#4211),
  // keyed by the MEETING's id: get only. The AI draft and the filing go through
  // the RAW Feathers client (MeetingInquiryStore), whose refusals reject.
  //   - no initial find (there is no find), no storage, no offline copy;
  //   - `throwOnError`: "could not read" must never look like "no open questions".
  {
    name: "meeting-inquiry-drafts",
    options: {
      idField: "id",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["get"],
      throwOnError: true,
    },
  },
  // Pending action-item assignments (review queue) — mutated via custom POSTs.
  // Also the per-module work rollup: rows carry `moduleId`, so Client 360 counts
  // open/total tracked work per module off this service (projects are gone).
  {
    name: "read-ai-assignments",
    options: { idField: "id", enableStorage: false },
  },
  // The extracted action items themselves (find/get only — the webhook and the
  // extraction stage author them, distribution mutates them). Read by Module 360's
  // Action Items tab, which passes the governed SPELLINGS it wants:
  // `{ governedSlug: [slug, ...aliases, ...legacyAliases] }`, optionally narrowed
  // by `clientId`. Offline-capable like its read-ai neighbours: `find` takes no
  // required param, so the wrapper's bare sync-on-reconnect find succeeds.
  //
  // `disableCreatedListener`: the API publishes this service's `created` to
  // staff (an open Meeting Detail follows a colleague's bulk close, #3820),
  // but a create here is always the close-bulk / close-stale ENVELOPE, never
  // an item, and merging it would put a row with no id in this cache. Its
  // `patched` (a close or reopen) IS the item row and is merged as usual.
  {
    name: "read-ai-action-items",
    options: { idField: "id", enableStorage: false, disableCreatedListener: true },
  },
  // Read.ai's OWN list — SUGGESTIONS, not work (2026-09-18). Find/get plus the
  // one write, `patch(id, { action: 'promote' })`, which is the only path from
  // the vendor's list into the action items. Never counted anywhere.
  // enableOffline stays ON by default like its read-ai neighbours: `find` takes
  // no required param, so the wrapper's sync-on-reconnect find succeeds.
  { name: "meeting-suggestions", options: { idField: "id", enableStorage: false } },
  // The list that REPLACED the approval queue: distributed work that cannot
  // reach a person, or cannot reach Azure DevOps. Find-only and DERIVED — there
  // is no table behind it. `enableOffline: false` for the `dashboard-summary` /
  // `capability-readiness` reason: find returns a BARE REPORT OBJECT
  // (`{ issues, counts, total, note, truncated }`), not a `{ data, total }`
  // envelope, so the offline wrapper's blanket bare find on reconnect cannot be
  // cached — and a cached copy of a derived list would be a claim about a
  // routing problem that may have been fixed since.
  {
    name: "action-routing-issues",
    options: { idField: "assignmentId", enableStorage: false, enableOffline: false },
  },
  // Singleton pipeline config (id "1"); patch-only, secrets write-only.
  { name: "read-ai-settings", options: { idField: "id", enableStorage: false } },

  // ── Parties, mapping, ledger, sources, delivery (epic Phases 3.5–7) ──────
  { name: "partners", options: { idField: "id", enableStorage: false } },
  { name: "client-email-domains", options: { idField: "id", enableStorage: false } },
  { name: "partner-email-domains", options: { idField: "id", enableStorage: false } },
  // Unmapped-meeting review queue (find + assign action); keyed by meetingId.
  // `find` rows carry a `mappingReason`; `patch` assigns the meeting to a CLIENT.
  { name: "meeting-mappings", options: { idField: "meetingId", enableStorage: false } },
  { name: "relationship-items", options: { idField: "id", enableStorage: false } },
  // Per-client happiness rollup (find-only); keyed by clientId.
  { name: "client-sentiment", options: { idField: "clientId", enableStorage: false } },
  { name: "meeting-source-connections", options: { idField: "id", enableStorage: false } },
  { name: "delivery-connections", options: { idField: "id", enableStorage: false } },
  // Per-meeting analysis outputs (summary/happiness/topics/…), read-only.
  { name: "meeting-analysis", options: { idField: "id", enableStorage: false } },
  // What a meeting SETTLED, as opposed to the work it created. Read-only on the
  // API (find only) — a decision is the account of what a meeting concluded, and
  // editing it here would be rewriting that record. `enableStorage: false` for
  // the same reason as meeting-analysis: it is loaded per meeting on demand, not
  // browsed as a collection.
  { name: "meeting-decisions", options: { idField: "id", enableStorage: false } },
  // The Gantry analysis queue. The web never polls or claims -- that is the
  // producer's job -- it uses TWO verbs from Meeting Detail:
  // `patch(id, { action: 'requeue' })` to retry a FAILED analysis, and
  // `patch(id, { action: 'reanalyse' })` to run a finished one again.
  { name: "meeting-analysis-queue", options: { idField: "id", enableStorage: false } },
  // WHO analysed a meeting, when, what they read and what they skipped. The
  // provenance behind the summary cards: `read_ai_analysis_runs` was written on
  // every accepted analysis and readable by nothing, so "was this done by HQ or
  // by Gantry, and did it include the video?" had no answer short of SQL.
  // Read-only (find), loaded per meeting on demand.
  { name: "meeting-analysis-runs", options: { idField: "id", enableStorage: false } },
  // Recording upload tickets (RPC pair, no entity list): create mints a SAS
  // URL the browser PUTs the video to directly — bytes never transit the API —
  // and patch commits/abandons the upload. See MeetingDetailStore.uploadRecording.
  // enableOffline OFF for the instance-actions reason: the API registers
  // methods ['create','patch'] only, so the offline wrapper's blanket find()
  // on every socket connect would 405 — and a queued-offline create would
  // "succeed" without ever minting a ticket.
  {
    name: "meeting-recording-uploads",
    options: { idField: "meetingId", enableStorage: false, enableOffline: false },
  },
  // Read.ai OAuth connect (find = status, create = start/finish). enableOffline
  // OFF for the instance-actions reason's WRITE half: a queued-offline create
  // would "mint" an authorize URL that never existed, and this surface stores a
  // live vendor credential — nothing about it may be replayed from a queue.
  {
    name: "read-ai-oauth",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  // Uploaded Outlook email threads (Client 360 "Comms" list), read-only.
  { name: "email-messages", options: { idField: "id", enableStorage: false } },

  // NOTE: the picker option sources the Settings / MappingQueue views read —
  // `clients` (registered in system.stores.ts, consumed via rootStore.clientsStore)
  // and `devops-connections` (registered in devops.stores.ts, consumed via
  // rootStore.devopsConnectionsStore) — are NOT re-declared here to avoid a
  // duplicate mount; those ViewStores read the existing stores off rootStore.
];
